import React from "react";

/**
 * White cat sprite — cream/white body with dark whiskers, pink accents.
 * Mood affects eye shape.
 */
export default function Cat({ dir = 1, sitting = false, size = 56, mood = "curious" }) {
  const body = "#fbf6eb";
  const outline = "#22202e";
  const belly = "#f8dee0"; // soft pink belly
  const earPink = "#ff87ab";
  const eyeColor = "#f4c242";
  const nosePink = "#ff5d8f";
  const whisker = "#22202e";
  const sleepy = sitting || mood === "sleepy";

  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      style={{ transform: `scaleX(${dir < 0 ? -1 : 1})`, transformOrigin: "center" }}
      aria-hidden
    >
      {/* shadow */}
      <ellipse cx="32" cy="58" rx="20" ry="3" fill="rgba(34,32,46,0.22)" />

      <g className="cat-body">
        {/* tail */}
        <path
          d="M 12 44 Q 4 40 6 30 Q 8 24 14 26"
          stroke={outline}
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
        />
        {/* legs */}
        <rect x="38" y="46" width="7" height="10" rx="2" fill={body} stroke={outline} strokeWidth="1.6" />
        <rect x="20" y="46" width="7" height="10" rx="2" fill={body} stroke={outline} strokeWidth="1.6" />
        {/* paws (pink pads) */}
        <ellipse cx="41.5" cy="55" rx="2.4" ry="1" fill={earPink} />
        <ellipse cx="23.5" cy="55" rx="2.4" ry="1" fill={earPink} />
        {/* body */}
        <path
          d="M 14 42 Q 14 28 26 24 L 42 24 Q 52 28 52 42 Q 52 52 32 52 Q 14 52 14 42 Z"
          fill={body}
          stroke={outline}
          strokeWidth="1.8"
        />
        {/* belly patch */}
        <path
          d="M 24 44 Q 24 38 32 38 Q 40 38 40 44 Q 40 50 32 50 Q 24 50 24 44 Z"
          fill={belly}
        />
        {/* head */}
        <path
          d="M 20 20 Q 20 10 32 10 Q 44 10 44 20 Q 44 30 32 30 Q 20 30 20 20 Z"
          fill={body}
          stroke={outline}
          strokeWidth="1.8"
        />
        {/* ears */}
        <path d="M 20 14 L 18 4 L 26 12 Z" fill={body} stroke={outline} strokeWidth="1.6" />
        <path d="M 44 14 L 46 4 L 38 12 Z" fill={body} stroke={outline} strokeWidth="1.6" />
        <path d="M 21 11 L 21 6 L 25 11 Z" fill={earPink} />
        <path d="M 43 11 L 43 6 L 39 11 Z" fill={earPink} />
        {/* eyes by mood */}
        {sleepy ? (
          <>
            <path d="M 26 20 Q 28 22 30 20" stroke={outline} strokeWidth="1.6" fill="none" strokeLinecap="round" />
            <path d="M 34 20 Q 36 22 38 20" stroke={outline} strokeWidth="1.6" fill="none" strokeLinecap="round" />
          </>
        ) : mood === "angry" ? (
          <>
            <path d="M 24.5 18 L 29 20 L 24.5 21" stroke={outline} strokeWidth="1.4" fill="none" />
            <path d="M 39.5 18 L 35 20 L 39.5 21" stroke={outline} strokeWidth="1.4" fill="none" />
            <circle cx="27" cy="20" r="1.6" fill={outline} />
            <circle cx="37" cy="20" r="1.6" fill={outline} />
          </>
        ) : mood === "excited" ? (
          <>
            <circle cx="27" cy="20" r="2.8" fill={eyeColor} />
            <circle cx="37" cy="20" r="2.8" fill={eyeColor} />
            <path d="M 25 18 L 29 22 M 29 18 L 25 22" stroke="#fff" strokeWidth="0.8" />
            <path d="M 35 18 L 39 22 M 39 18 L 35 22" stroke="#fff" strokeWidth="0.8" />
            <circle cx="27" cy="20" r="1" fill={outline} />
            <circle cx="37" cy="20" r="1" fill={outline} />
          </>
        ) : (
          <>
            <circle cx="27" cy="20" r="2.4" fill={eyeColor} />
            <circle cx="37" cy="20" r="2.4" fill={eyeColor} />
            <circle cx="27.4" cy="20.2" r="0.9" fill={outline} />
            <circle cx="37.4" cy="20.2" r="0.9" fill={outline} />
            <circle cx="26.4" cy="19.2" r="0.5" fill="#fff" />
            <circle cx="36.4" cy="19.2" r="0.5" fill="#fff" />
          </>
        )}
        {/* nose */}
        <path d="M 31 22 L 33 22 L 32 24 Z" fill={nosePink} />
        {/* mouth by mood */}
        {mood === "excited" ? (
          <path d="M 32 24 Q 30 27 27 26 M 32 24 Q 34 27 37 26" stroke={outline} strokeWidth="1.2" fill="none" strokeLinecap="round" />
        ) : mood === "angry" ? (
          <path d="M 28 26 Q 32 24 36 26" stroke={outline} strokeWidth="1.2" fill="none" strokeLinecap="round" />
        ) : (
          <path d="M 32 24 Q 30 26 28 25 M 32 24 Q 34 26 36 25" stroke={outline} strokeWidth="1" fill="none" strokeLinecap="round" />
        )}
        {/* whiskers */}
        <path d="M 22 22 L 16 21 M 22 24 L 16 25" stroke={whisker} strokeWidth="0.7" />
        <path d="M 42 22 L 48 21 M 42 24 L 48 25" stroke={whisker} strokeWidth="0.7" />
        {/* blush spots when playful/excited */}
        {(mood === "excited" || mood === "playful") && (
          <>
            <circle cx="24" cy="24" r="1.8" fill={earPink} opacity="0.55" />
            <circle cx="40" cy="24" r="1.8" fill={earPink} opacity="0.55" />
          </>
        )}
      </g>
    </svg>
  );
}
